import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Logo } from '@/shared/components/logo'
import { useAuthModal } from '@/features/auth'
import { useSmoothScroll } from '../hooks'

/**
 * Landing page header with sticky navigation
 * Implementation: LAND2-008
 */
export function Header() {
  const { t } = useTranslation()
  const { openAuthModal } = useAuthModal()
  const { scrollToHowItWorks, scrollToPricing, scrollToFaq, scrollToTop } = useSmoothScroll()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Detect scroll for backdrop blur effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu when clicking a nav link
  const handleNavClick = (scrollFn: () => void) => {
    scrollFn()
    setIsMobileMenuOpen(false)
  }

  // Handle CTA clicks - both open the same unified modal
  const handleSignIn = () => {
    openAuthModal()
    setIsMobileMenuOpen(false)
  }

  const handleStartScoping = () => {
    openAuthModal()
    setIsMobileMenuOpen(false)
  }

  const navLinks = [
    { label: t('landing.header.howItWorks'), onClick: () => handleNavClick(scrollToHowItWorks) },
    { label: t('landing.header.pricing'), onClick: () => handleNavClick(scrollToPricing) },
    { label: t('landing.header.faq'), onClick: () => handleNavClick(scrollToFaq) },
  ]

  return (
    <header
      className={cn(
        'sticky top-0 z-dropdown w-full transition-all duration-200',
        isScrolled
          ? 'bg-white/90 shadow-sm backdrop-blur-md'
          : 'bg-white'
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <button
          onClick={scrollToTop}
          className="flex items-center gap-2.5 text-foreground transition-colors hover:text-accent"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <Logo className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-bold">HeyNicolai</span>
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={link.onClick}
              className="text-sm font-medium text-foreground-muted transition-colors hover:text-foreground"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden items-center gap-3 md:flex">
          <button
            onClick={handleSignIn}
            className="rounded-lg px-4 py-2 text-sm font-medium text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground"
          >
            {t('landing.header.signIn')}
          </button>
          <button
            onClick={handleStartScoping}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
          >
            {t('landing.header.startScoping')}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-background-secondary md:hidden"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="border-t border-border bg-white md:hidden">
          <nav className="flex flex-col px-4 py-4">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={link.onClick}
                className="py-3 text-left text-sm font-medium text-foreground-muted transition-colors hover:text-foreground"
              >
                {link.label}
              </button>
            ))}
            <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
              <button
                onClick={handleSignIn}
                className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
              >
                {t('landing.header.signIn')}
              </button>
              <button
                onClick={handleStartScoping}
                className="rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
              >
                {t('landing.header.startScoping')}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
